import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Shuffle, Sun, Moon, Database } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";
import { generateFullName } from "@/utils/brazilianData";
import { generateEasyCPF } from "@/utils/easyCpf";

function NomeCpfPage() {
  const [darkMode, setDarkMode] = useState(false);
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [copiedField, setCopiedField] = useState(null);
  const [noFormatting, setNoFormatting] = useState(true);

  const cpfDisplay = noFormatting ? cpf.replace(/\D/g, "") : cpf;

  const gerarNovo = useCallback(() => {
    setNome(generateFullName());
    setCpf(generateEasyCPF());
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    }
    // Gera automaticamente ao abrir
    gerarNovo();
  }, [gerarNovo]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  const copyText = async (text, field) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-999999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (_) {
      // Silent
    }
  };

  const copyBoth = () => copyText(`Nome: ${nome}\nCPF: ${cpfDisplay}`, "both");

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] flex items-center justify-center shadow-lg shadow-primary/20">
              <Database size={18} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">
                Gerador de Nome e CPF
              </h1>
              <p className="text-[11px] text-muted-foreground -mt-0.5 hidden sm:block">
                Ferramenta rápida do ClickDados
              </p>
            </div>
          </div>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            className="w-9 h-9 rounded-xl bg-secondary hover:bg-accent flex items-center justify-center transition-all duration-200 hover:scale-105"
            aria-label="Toggle theme"
          >
            {darkMode ? (
              <Sun size={18} weight="bold" className="text-amber-500" />
            ) : (
              <Moon size={18} weight="bold" className="text-slate-600" />
            )}
          </button>
        </div>
      </header>

      {/* Voltar */}
      <div className="max-w-5xl w-full mx-auto px-6 lg:px-8 pt-6">
        <Link
          to="/"
          data-testid="back-to-home-link"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={16} weight="bold" />
          Voltar para o ClickDados
        </Link>
      </div>

      {/* Main */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 lg:px-8 py-10 sm:py-14">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
            Nome e CPF Aleatórios
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Dados fictícios prontos para uso em testes de formulários
          </p>
        </div>

        {/* Cards em destaque */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          {/* Card Nome */}
          <motion.div
            key={`nome-${nome}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-card rounded-2xl border border-border/50 shadow-sm p-8 sm:p-10 flex flex-col justify-between min-h-[220px]"
            data-testid="card-nome"
          >
            <div>
              <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-widest">
                Nome
              </label>
              <div
                data-testid="value-nome"
                className="mt-3 font-semibold tracking-tight text-2xl sm:text-3xl md:text-4xl leading-tight break-words"
              >
                {nome}
              </div>
            </div>
            <button
              data-testid="copy-nome-btn"
              onClick={() => copyText(nome, "nome")}
              className="mt-6 h-11 px-5 rounded-xl border border-border/50 bg-secondary/50 hover:bg-secondary transition-all duration-200 flex items-center justify-center gap-2 font-medium text-sm self-start"
            >
              {copiedField === "nome" ? (
                <>
                  <Check size={16} weight="bold" className="text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400">Copiado!</span>
                </>
              ) : (
                <>
                  <Copy size={16} weight="bold" />
                  Copiar nome
                </>
              )}
            </button>
          </motion.div>

          {/* Card CPF */}
          <motion.div
            key={`cpf-${cpf}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.05 }}
            className="bg-card rounded-2xl border border-border/50 shadow-sm p-8 sm:p-10 flex flex-col justify-between min-h-[220px]"
            data-testid="card-cpf"
          >
            <div>
              <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-widest">
                CPF
              </label>
              <div
                data-testid="value-cpf"
                className="mt-3 font-mono font-bold tracking-wider text-4xl sm:text-3xl md:text-4xl leading-tight break-words"
              >
                {cpfDisplay}
              </div>
            </div>
            <button
              data-testid="copy-cpf-btn"
              onClick={() => copyText(cpfDisplay, "cpf")}
              className="mt-6 h-11 px-5 rounded-xl border border-border/50 bg-secondary/50 hover:bg-secondary transition-all duration-200 flex items-center justify-center gap-2 font-medium text-sm self-start"
            >
              {copiedField === "cpf" ? (
                <>
                  <Check size={16} weight="bold" className="text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400">Copiado!</span>
                </>
              ) : (
                <>
                  <Copy size={16} weight="bold" />
                  Copiar CPF
                </>
              )}
            </button>
          </motion.div>
        </div>

        {/* Toggle formatação */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <Checkbox
            id="no-formatting"
            data-testid="no-formatting-checkbox"
            checked={noFormatting}
            onCheckedChange={setNoFormatting}
            className="rounded-md border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary checkbox-modern"
          />
          <label
            htmlFor="no-formatting"
            className="text-sm text-muted-foreground cursor-pointer select-none"
          >
            CPF sem pontuação (apenas números)
          </label>
        </div>

        {/* Ações */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            data-testid="generate-new-btn"
            onClick={gerarNovo}
            className="btn-primary flex-1 py-4 sm:py-0 sm:h-12 px-8 rounded-xl text-white font-medium flex items-center justify-center gap-2.5"
          >
            <Shuffle size={18} weight="bold" />
            Gerar Novo
          </button>
          <button
            data-testid="copy-both-btn"
            onClick={copyBoth}
            className="flex-1 h-12 px-6 rounded-xl border border-border/50 bg-card hover:bg-accent transition-all duration-200 flex items-center justify-center gap-2.5 font-medium card-hover"
          >
            {copiedField === "both" ? (
              <>
                <Check size={18} weight="bold" className="text-emerald-500" />
                <span className="text-emerald-600 dark:text-emerald-400">Copiado!</span>
              </>
            ) : (
              <>
                <Copy size={18} weight="bold" />
                Copiar Nome + CPF
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <footer className="mt-14 pt-8 border-t border-border/50">
          <div className="text-center space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed max-w-md mx-auto">
              CPF matematicamente válido, porém fictício. Nenhum dado é salvo ou enviado para APIs externas.
            </p>
            <p className="text-xs text-muted-foreground/50">
              Desenvolvido por Erickson
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default NomeCpfPage;
